import * as amplitude from "@amplitude/analytics-browser";
import { RedoCheckbox } from "@redotech/arbiter/checkbox/redo-checkbox";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { memo, useState } from "react";
import { ConfirmDeleteSupportV2FolderModal } from "./confirm-delete-support-v2-folder-modal";

interface SupportV2FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    folderName: string,
    selectedViewIds: string[],
    isPrivate: boolean,
  ) => Promise<void>;
  onDelete?: () => Promise<void>;
  reservedFolderNames: Set<string>;
  currentName?: string;
  currentViewIds?: string[];
  mode: "create" | "edit";
  views: { id: string; name: string }[];
}

export const SupportV2FolderModal = memo(function SupportV2FolderModal({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  reservedFolderNames,
  currentName = "",
  currentViewIds = [],
  mode,
  views,
}: SupportV2FolderModalProps) {
  const [folderName, setFolderName] = useState(currentName);
  const [selectedViewIds, setSelectedViewIds] =
    useState<string[]>(currentViewIds);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      amplitude.logEvent(
        mode === "create"
          ? "create-support-ticket-folder"
          : "update-support-ticket-folder",
        {},
      );
      await onSubmit(folderName, selectedViewIds, isPrivate);
      onClose();
    } finally {
      setIsSubmitting(false);
      if (mode === "create") {
        setFolderName("");
        setSelectedViewIds([]);
        setIsPrivate(false);
      }
    }
  };

  const isNameReserved =
    reservedFolderNames.has(folderName.trim()) &&
    (mode === "create" || folderName.trim() !== currentName);

  const toggleView = (viewId: string) => {
    setSelectedViewIds((prev) =>
      prev.includes(viewId)
        ? prev.filter((id) => id !== viewId)
        : [...prev, viewId],
    );
  };

  const primaryButton: RedoModalButtonProps = {
    text: mode === "create" ? "Create" : "Submit",
    onClick: handleSubmit,
    disabled: !folderName.trim().length || isNameReserved || isSubmitting,
    pending: isSubmitting,
    size: "sm",
  };

  const secondaryButton: RedoModalButtonProps =
    mode === "create"
      ? { text: "Cancel", onClick: onClose, disabled: isSubmitting, size: "sm" }
      : {
          text: "Delete",
          onClick: () => setIsConfirmDeleteOpen(true),
          disabled: isSubmitting,
          theme: "destructive",
          size: "sm",
        };

  return (
    <>
      <RedoModal
        isOpen={isOpen}
        modalSize="sm"
        onModalCloseRequested={isSubmitting ? () => {} : onClose}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        title={mode === "create" ? "Create folder" : "Edit folder"}
      >
        <Flex dir="column" gap="xl">
          <RedoTextInput
            label="Name"
            setValue={setFolderName}
            size="sm"
            state={isSubmitting ? "readonly" : "default"}
            value={folderName}
          />
          <Flex dir="column" gap="sm">
            <Text>{mode === "create" ? "Select Views" : "Select views"}</Text>
            {views.map((view) => (
              <RedoCheckbox
                key={view.id}
                label={view.name}
                setValue={() => toggleView(view.id)}
                value={selectedViewIds.includes(view.id)}
              />
            ))}
            {views.length === 0 && (
              <Text textColor="secondary">No views available</Text>
            )}
          </Flex>
          {mode === "create" && (
            <RedoCheckbox
              label="Only visible to me"
              setValue={(val) => setIsPrivate(val === true)}
              value={isPrivate}
            />
          )}
        </Flex>
      </RedoModal>
      {mode === "edit" && onDelete && (
        <ConfirmDeleteSupportV2FolderModal
          folderName={folderName}
          isOpen={isConfirmDeleteOpen}
          onClose={() => setIsConfirmDeleteOpen(false)}
          onSubmit={onDelete!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        />
      )}
    </>
  );
});
